import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Spin } from "antd";
import { ArrowDownOutlined, SearchOutlined, UserOutlined, CheckCircleFilled, UserAddOutlined } from "@ant-design/icons";

import { controller } from "./controller";
import NewPatientForm from "./NewPatientForm";
import { formatDob, isNameQuery, prefillFromQuery } from "./newPatient";

/**
 * Step 1 — who is this for.
 *
 * The step is ONE action (find a patient), so the whole panel is built around
 * making that unmissable: the step's job is stated as a question, the input is
 * the largest thing on screen, and the results area is a permanent bordered
 * panel rather than empty space — so a user who has not typed yet can still see
 * where their results are going to appear and what the field is for.
 *
 * Deliberately NOT pre-populated with a "recent patients" list. The office's
 * unfiltered patient list leads with PMS placeholder rows ("? Dunbrack",
 * "00000 0000", "1 OP"), so landing on it would teach the wrong thing and look
 * broken. An empty state that explains itself beats a list of junk.
 *
 * Keyboard-first: this is the step a front-desk user repeats all day, so
 * ↑/↓/Enter completes it without touching the mouse. Search runs against
 * `PatientList` through the shared `patientSearchParams` routing, so what a
 * given query matches is identical to the Patients board.
 *
 * A patient the office has only just added to its PMS isn't here until the
 * nightly sync, so the step also adds one (NewPatientForm, in place of the
 * search): as the answer to a search that found nobody, and from a quiet link
 * under the field for when the right person just isn't among the matches.
 */
export default function PatientPicker({ selected, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  // Starting values for the new-patient form while it is open; null otherwise.
  const [adding, setAdding] = useState(null);
  const inputRef = useRef(null);
  // Guards against a slow early request landing after a later one.
  const reqSeq = useRef(0);

  useEffect(function () {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const run = useCallback(async function runInner(q) {
    const seq = ++reqSeq.current;
    if (!q || q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await controller.searchPatients(q);
      if (seq !== reqSeq.current) return; // a newer query already answered
      const body = (res && res.json) || {};
      const rows = Array.isArray(body) ? body : body.results || [];
      setResults(rows);
      setActive(0);
    } catch (e) {
      if (seq === reqSeq.current) setResults([]);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  // Debounced so typing a full name doesn't fire a request per keystroke.
  useEffect(
    function () {
      const t = setTimeout(function () {
        run(query);
      }, 280);
      return function () {
        clearTimeout(t);
      };
    },
    [query, run],
  );

  function handleKeyDown(e) {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(function (i) { return Math.min(i + 1, results.length - 1); });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(function (i) { return Math.max(i - 1, 0); });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = results[active];
      if (row) onSelect(row);
    }
  }

  function nameOf(p) {
    return [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unnamed patient";
  }

  const typed = query.trim();
  const noMatch = !loading && !results.length && typed.length >= 2;

  function startAdding() {
    setAdding(typed.length >= 2 ? prefillFromQuery(typed) : {});
  }

  // --- The patient is chosen: confirm it and get out of the way. -------------
  if (selected) {
    return (
      <div>
        <div className="ir-field-label">Patient</div>
        <div className="ir-chosen">
          <CheckCircleFilled className="ir-chosen-tick" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ir-chosen-name">{nameOf(selected)}</div>
            <div className="ir-chosen-meta">
              {selected.birth_date
                ? "Date of birth " + formatDob(selected.birth_date)
                : "No date of birth on file"}
            </div>
          </div>
          <Button type="link" onClick={function () { onSelect(null); }} className="ir-chosen-change">
            Change
          </Button>
        </div>
      </div>
    );
  }

  // --- Adding someone who isn't here yet. ------------------------------------
  // The search is kept (state lives here), so Cancel returns to it as it was.
  if (adding) {
    return (
      <NewPatientForm
        initial={adding}
        onSelect={function (p) {
          setAdding(null);
          onSelect(p);
        }}
        onCancel={function () { setAdding(null); }}
      />
    );
  }

  // --- Nothing chosen yet: make the one action obvious. ----------------------
  //
  // The instruction leads and the search box follows it, with an arrow bridging
  // the two. An empty results panel could only show where answers WOULD appear;
  // a card that names the task and points at the control tells you what to do.
  return (
    <div>
      <div className="ir-guide">
        <div className="ir-guide-icon"><UserOutlined /></div>
        <p className="ir-guide-title">Select a Patient</p>
        <p className="ir-guide-sub">
          Search by name, phone number or email to find who this request is for.
        </p>
        {/* Sits on the card's bottom edge, aimed at the field below. */}
        <span className="ir-guide-arrow" aria-hidden="true">
          <ArrowDownOutlined />
        </span>
      </div>

      <Input
        ref={inputRef}
        size="large"
        allowClear
        autoComplete="off"
        className="ir-search-field"
        prefix={<SearchOutlined style={{ color: "#848696", fontSize: 17 }} />}
        placeholder="Search patients…"
        value={query}
        onChange={function (e) { setQuery(e.target.value); }}
        onKeyDown={handleKeyDown}
      />

      {/* Only rendered once there is something to say — the guide card above now
          carries the instruction, so an idle placeholder here would repeat it. */}
      {loading ? (
        <div className="ir-search-panel">
          <div className="ir-panel-state">
            <Spin size="small" />
            <p className="ir-panel-sub" style={{ marginTop: 12 }}>Searching…</p>
          </div>
        </div>
      ) : results.length ? (
        <div className="ir-search-panel">
          <div className="ir-panel-count">
            {results.length} {results.length === 1 ? "match" : "matches"} — select one
          </div>
          {results.map(function (p, i) {
            return (
              <div
                key={p.id}
                className={"ir-result" + (i === active ? " is-active" : "")}
                onMouseEnter={function () { setActive(i); }}
                onClick={function () { onSelect(p); }}
              >
                <div>
                  <div className="ir-result-name">{nameOf(p)}</div>
                  <div className="ir-result-meta">
                    {p.birth_date ? formatDob(p.birth_date) : "No DOB"}
                    {p.phone ? " · " + p.phone : ""}
                  </div>
                </div>
                <span className="ir-result-pick">Select</span>
              </div>
            );
          })}
        </div>
      ) : noMatch ? (
        // Nobody: most often a patient the PMS has and we don't yet, so adding
        // them is the way forward — not a dead end with spelling advice.
        <div className="ir-search-panel">
          <div className="ir-panel-state">
            <div className="ir-panel-icon"><SearchOutlined /></div>
            <p className="ir-panel-title">No patients match “{typed}”</p>
            <p className="ir-panel-sub" style={{ marginBottom: 16 }}>
              Check the spelling, or try their phone number or email address. New to
              your practice, or not synced from your PMS yet? Add them here.
            </p>
            <Button type="primary" icon={<UserAddOutlined />} onClick={startAdding}>
              {isNameQuery(typed) ? "Add “" + typed + "” as a new patient" : "Add a new patient"}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Always on offer — the right person may simply not be among the matches.
          Left out under "no match", whose own button says the same thing. */}
      {noMatch ? null : (
        <p className="ir-add-patient-line">
          Can't find them?{" "}
          <button type="button" className="ir-link" onClick={startAdding}>
            Add a new patient
          </button>
        </p>
      )}
    </div>
  );
}
